import { FC, useState } from 'react';
import { ProcessedResult } from './filigrane.types';
import FiligraneCard, { FiligraneCardSize } from './FiligraneCard';
import SendFileModal from './SendFileModal';
import './StepResult.scss';
import './WatermarkProcessing.scss';
import iconDownload from 'assets/img/icons/icon_Download.svg';

import { ReactComponent as IconTrash } from "assets/img/icons/icon_Trash2.svg";
import { ReactComponent as IconAddFile } from "assets/img/icons/icon_AddFile.svg";
import { ReactComponent as IconSend } from "assets/img/icons/icon_Send.svg";

interface Props {
  results: ProcessedResult[];
  selectedIds: string[];
  failedFiles?: { name: string; reason: string }[];
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onDelete: (id: string) => void;
  onDownloadSelected: () => void;
  onReset: () => void;
}

const StepResult: FC<Props> = ({
  results,
  selectedIds,
  failedFiles = [],
  onToggle,
  onSelectAll,
  onDelete,
  onDownloadSelected,
  onReset,
}) => {
  const allSelected = results.length > 0 && selectedIds.length === results.length;
  const [sendModalOpen, setSendModalOpen] = useState(false);

  // 1 file → xl centered · 2 → lg · 3 → md · 4+ → sm
  const cardSize: FiligraneCardSize =
    results.length === 1 ? 'xl' : results.length === 2 ? 'lg' : results.length === 3 ? 'md' : 'sm';

  const selectedResults = results.filter(r => selectedIds.includes(r.id));

  const handleDeleteSelected = () => {
    results
      .filter(r => selectedIds.includes(r.id))
      .forEach(r => onDelete(r.id));
  };

  return (
    <div className="step-result">
      <div className="step-result__area">
        {/* Toolbar */}
        {results.length !== 0 && (
          <div className="step-result__toolbar">
            <button
              className="step-result__select-all"
              onClick={onSelectAll}
              type="button"
              aria-label="Tout sélectionner"
            >
              <div className={`step-result__checkbox-box${allSelected ? ' step-result__checkbox-box--checked' : ''}`}>
                {allSelected && <span>✓</span>}
              </div>
              <span className="step-result__label">tout sélectionner</span>
            </button>
            <div className="step-result__divider" aria-hidden="true" />
            <button
              className="step-result__delete-all-btn"
              onClick={handleDeleteSelected}
              type="button"
              disabled={selectedIds.length === 0}
            >
              <IconTrash fill={"#FF5326"}/>
              supprimer
            </button>
          </div>
        )}

        {/* Fichiers en échec — affichés au lieu d'être silencieusement ignorés */}
        {failedFiles.length > 0 && (
          <div className="step-result__errors" role="alert">
            <p className="step-result__errors-title">
              {failedFiles.length} fichier{failedFiles.length > 1 ? 's' : ''} n'{failedFiles.length > 1 ? 'ont' : 'a'} pas pu être traité{failedFiles.length > 1 ? 's' : ''} :
            </p>
            <ul className="step-result__errors-list">
              {failedFiles.map((f, i) => (
                <li key={i}>
                  <strong>{f.name}</strong> — {f.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Result grid */}
        <div className={`step-result__grid step-result__grid--animated${results.length === 1 ? ' step-result__grid--single' : ''}`}>
          {results.length === 0
            ? (
              <p className="step-result__empty">
                {failedFiles.length > 0
                  ? "Aucun fichier n'a pu être traité."
                  : 'Tous les fichiers ont été supprimés.'}
              </p>
            )
            : results.map((result, index) => (
                <FiligraneCard
                  key={result.id}
                  id={result.id}
                  name={result.name}
                  previewUrl={result.previewUrl}
                  fileType={result.type}
                  isSelected={selectedIds.includes(result.id)}
                  onToggle={onToggle}
                  variant="result"
                  size={cardSize}
                  onDelete={onDelete}
                  scanDelay={0.38 + index * 0.055 + 0.1}
                />
              ))
          }
        </div>

        {/* Actions */}
        <div className="step-result__actions">
          <button className="step-result__add-btn" onClick={onReset} type="button">
            <IconAddFile />
            <span>ajouter</span>
          </button>
          <button
            className="step-result__send-btn"
            onClick={() => setSendModalOpen(true)}
            type="button"
            disabled={selectedIds.length === 0}
          >
            <IconSend fill="#1956A8" width={16} />
            <span>envoyer</span>
          </button>
          <button
            className="step-result__download-btn"
            onClick={onDownloadSelected}
            type="button"
            disabled={selectedIds.length === 0}
          >
            <img src={iconDownload} alt="" />
            <span>télécharger</span>
          </button>
        </div>
      </div>

      <SendFileModal
        isOpen={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        results={selectedResults}
      />
    </div>
  );
};

export default StepResult;
